// CRUD admin de bonos vendidos a alumnos. Tabla con filtros + modal venta +
// modal ajustar (creditos restantes / fecha caducidad).

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus, Edit2, X, AlertCircle, Loader2, Tag, CheckCircle2, Trash2,
} from 'lucide-react';
import { createPortal } from 'react-dom';

import {
  listBonosComprados, createBonoComprado, updateBonoComprado, cancelarBonoComprado,
  listBonosTipos,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';

function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES');
}

// Renderiza la celda de caducidad: fecha + chip "X días" si está dentro
// de los próximos 14 días (urgente, clay).
function CaducidadCell({ iso }) {
  if (!iso) return <span>—</span>;
  const t = new Date(iso).getTime();
  const dias = Math.round((t - Date.now()) / 86400000);
  const urgente = dias >= 0 && dias <= 14;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <span>{fmtFecha(iso)}</span>
      {urgente && (
        <span className="estado-pill" style={{ background: 'rgba(200, 125, 91, 0.15)', color: 'var(--clay)', borderColor: 'rgba(200, 125, 91, 0.4)', fontSize: 10 }}>
          {dias === 0 ? 'hoy' : dias === 1 ? '1 día' : `${dias} días`}
        </span>
      )}
    </span>
  );
}

function badgeEstado(e) {
  const cls = {
    activo: 'estado-pill estado-pill-ok',
    caducado: 'estado-pill',
    cancelado: 'estado-pill estado-pill-err',
  }[e] || 'estado-pill';
  return cls;
}

export default function AdminBonosComprados() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [bonosTipos, setBonosTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vender, setVender] = useState(false);
  const [ajustar, setAjustar] = useState(null); // {bono}
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [caducanPronto, setCaducanPronto] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const filtros = {};
      if (caducanPronto) {
        filtros.caduca_en_dias = 14;          // override estado/etc; backend asume vigentes
      } else {
        if (filtroEstado !== 'todos') filtros.estado = filtroEstado;
      }
      if (busqueda) filtros.busqueda_email = busqueda;
      const [r, tipos] = await Promise.all([
        listBonosComprados(filtros),
        listBonosTipos(),
      ]);
      setItems(r.data || []);
      setBonosTipos(tipos.data || []);
    } catch (e) { setError(e); } finally { setLoading(false); }
  }, [filtroEstado, busqueda, caducanPronto]);
  useEffect(() => { cargar(); }, [cargar]);

  const onCancelar = async (b) => {
    const nombre = b.alumno?.email || b.alumno?.nombre || '—';
    if (!window.confirm(t('admin.bonos_comprados.confirmar_cancelar', { alumno: nombre }))) return;
    try {
      await cancelarBonoComprado(b.id);
      await cargar();
    } catch (e) { alert(e.message || t('admin.error_generico')); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.bonos_comprados.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.bonos_comprados.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setVender(true)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.bonos_comprados.vender_nuevo')}
        </button>
      </header>

      <div className="admin-filtros admin-filtros-inline">
        <label className="admin-filtro-campo">
          <span>{t('admin.bonos_comprados.filtro_estado')}</span>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            disabled={caducanPronto}
          >
            <option value="todos">{t('admin.bonos_comprados.estados.todos')}</option>
            <option value="activo">{t('admin.bonos_comprados.estados.activo')}</option>
            <option value="caducado">{t('admin.bonos_comprados.estados.caducado')}</option>
            <option value="cancelado">{t('admin.bonos_comprados.estados.cancelado')}</option>
          </select>
        </label>
        <button
          type="button"
          className={'admin-filtro-pill' + (caducanPronto ? ' is-active' : '')}
          onClick={() => setCaducanPronto((v) => !v)}
          title={t('admin.bonos_comprados.filtro_caducan_pronto_hint')}
        >
          {t('admin.bonos_comprados.filtro_caducan_pronto')}
        </button>
        <label className="admin-filtro-campo" style={{ flex: 1, minWidth: 200 }}>
          <span>{t('admin.bonos_comprados.busqueda_email')}</span>
          <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="email@ejemplo.com" />
        </label>
      </div>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}

      {!loading && !error && items.length === 0 && (
        <div className="admin-empty">{t('admin.bonos_comprados.vacio')}</div>
      )}

      {!loading && !error && items.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th>{t('admin.bonos_comprados.col.alumno')}</th>
              <th>{t('admin.bonos_comprados.col.bono')}</th>
              <th className="num">{t('admin.bonos_comprados.col.creditos')}</th>
              <th>{t('admin.bonos_comprados.col.compra')}</th>
              <th>{t('admin.bonos_comprados.col.caducidad')}</th>
              <th>{t('admin.bonos_comprados.col.estado')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id}>
                <td>
                  <div>{[b.alumno?.nombre, b.alumno?.apellidos].filter(Boolean).join(' ') || '—'}</div>
                  <div className="dim" style={{ display: 'block' }}>{b.alumno?.email || ''}</div>
                </td>
                <td>{b.bono_tipo?.nombre_es || '—'}</td>
                <td className="num">
                  {b.es_membresia
                    ? t('admin.bonos_comprados.ilimitado')
                    : `${b.creditos_restantes} / ${b.creditos_iniciales}`}
                </td>
                <td>{fmtFecha(b.fecha_compra)}</td>
                <td><CaducidadCell iso={b.fecha_caducidad} /></td>
                <td><span className={badgeEstado(b.estado)}>{t('admin.bonos_comprados.estados.' + b.estado)}</span></td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setAjustar({ bono: b })} aria-label={t('admin.editar')}>
                    <Edit2 size={16} strokeWidth={1.5} />
                  </button>
                  {b.estado === 'activo' && (
                    <button type="button" onClick={() => onCancelar(b)} aria-label={t('admin.bonos_comprados.cancelar_bono')}>
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {vender && (
        <ModalVender
          bonosTipos={bonosTipos}
          onCerrar={() => setVender(false)}
          onGuardado={() => { setVender(false); cargar(); }}
        />
      )}
      {ajustar && (
        <ModalAjustar
          bono={ajustar.bono}
          onCerrar={() => setAjustar(null)}
          onGuardado={() => { setAjustar(null); cargar(); }}
        />
      )}
    </div>
  );
}

function ModalVender({ bonosTipos, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    email_alumno: '',
    bono_tipo_id: '',
    pago_id: '',
    precio_pagado_euros: '',
  });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const tipoSel = bonosTipos.find((b) => b.id === form.bono_tipo_id);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const payload = {
        email_alumno: form.email_alumno.trim().toLowerCase(),
        bono_tipo_id: form.bono_tipo_id,
      };
      if (form.pago_id.trim()) payload.pago_id = form.pago_id.trim();
      if (form.precio_pagado_euros !== '') payload.precio_pagado_euros = Number(form.precio_pagado_euros);
      await createBonoComprado(payload);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e.message || t('admin.error_generico')));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo"><Tag size={20} style={{ marginRight: 8 }} />{t('admin.bonos_comprados.titulo_vender')}</h2>

        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo full">
              <label>{t('admin.bonos_comprados.f.email_alumno')}</label>
              <input
                type="email"
                value={form.email_alumno}
                onChange={(e) => setForm((f) => ({ ...f, email_alumno: e.target.value }))}
                placeholder="alumno@ejemplo.com"
                required
              />
              <small className="admin-hint">{t('admin.bonos_comprados.f.email_hint')}</small>
            </div>
            <div className="modal-campo full">
              <label>{t('admin.bonos_comprados.f.bono_tipo')}</label>
              <select
                value={form.bono_tipo_id}
                onChange={(e) => setForm((f) => ({ ...f, bono_tipo_id: e.target.value }))}
                required
              >
                <option value="">—</option>
                {bonosTipos.filter((b) => b.activo).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.nombre_es} · {Number(b.precio_euros).toFixed(2)} €
                    {b.creditos_incluidos != null ? ` · ${b.creditos_incluidos} ${t('admin.bonos_comprados.creditos')}` : ''}
                    {b.validez_dias ? ` · ${b.validez_dias}d` : ''}
                  </option>
                ))}
              </select>
            </div>
            {tipoSel && (
              <div className="modal-campo full">
                <small className="admin-hint">
                  {tipoSel.tipo?.startsWith('membresia')
                    ? t('admin.bonos_comprados.preview_membresia')
                    : t('admin.bonos_comprados.preview_bono', {
                        creditos: tipoSel.creditos_incluidos,
                        validez: tipoSel.validez_dias || '—',
                      })}
                </small>
              </div>
            )}
            <div className="modal-campo">
              <label>{t('admin.bonos_comprados.f.pago_id')}</label>
              <input
                type="text"
                value={form.pago_id}
                onChange={(e) => setForm((f) => ({ ...f, pago_id: e.target.value }))}
                placeholder="transf-..."
                maxLength={200}
              />
            </div>
            <div className="modal-campo">
              <label>{t('admin.bonos_comprados.f.precio_pagado')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.precio_pagado_euros}
                onChange={(e) => setForm((f) => ({ ...f, precio_pagado_euros: e.target.value }))}
                placeholder={tipoSel ? Number(tipoSel.precio_euros).toFixed(2) : ''}
              />
              <small className="admin-hint">{t('admin.bonos_comprados.f.precio_hint')}</small>
            </div>
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : <><CheckCircle2 size={16} /> {t('admin.bonos_comprados.confirmar_venta')}</>}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function ModalAjustar({ bono, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    creditos_restantes: bono.creditos_restantes,
    fecha_caducidad: bono.fecha_caducidad ? bono.fecha_caducidad.slice(0, 10) : '',
    estado: bono.estado,
  });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setEnviando(true);
    try {
      const payload = {};
      if (Number(form.creditos_restantes) !== bono.creditos_restantes) payload.creditos_restantes = Number(form.creditos_restantes);
      const fechaIso = form.fecha_caducidad ? new Date(form.fecha_caducidad + 'T23:59:59Z').toISOString() : null;
      if (fechaIso !== bono.fecha_caducidad) payload.fecha_caducidad = fechaIso;
      if (form.estado !== bono.estado) payload.estado = form.estado;
      if (Object.keys(payload).length === 0) { onCerrar(); return; }
      await updateBonoComprado(bono.id, payload);
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : (e.message || t('admin.error_generico')));
    } finally { setEnviando(false); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar}><X size={20} /></button>
        <h2 className="modal-titulo">{t('admin.bonos_comprados.titulo_ajustar')}</h2>

        <div className="pagos-modal-resumen">
          <div><span className="dim">{t('admin.bonos_comprados.col.alumno')}</span><strong>{bono.alumno?.email || '—'}</strong></div>
          <div><span className="dim">{t('admin.bonos_comprados.col.bono')}</span><strong>{bono.bono_tipo?.nombre_es || '—'}</strong></div>
          <div><span className="dim">{t('admin.bonos_comprados.f.iniciales')}</span><strong>{bono.creditos_iniciales}</strong></div>
          <div><span className="dim">{t('admin.bonos_comprados.col.compra')}</span><strong>{fmtFecha(bono.fecha_compra)}</strong></div>
        </div>

        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <div className="modal-campo">
              <label>{t('admin.bonos_comprados.f.creditos_restantes')}</label>
              <input
                type="number"
                min="0"
                max={bono.creditos_iniciales}
                value={form.creditos_restantes}
                onChange={(e) => setForm((f) => ({ ...f, creditos_restantes: e.target.value }))}
              />
              <small className="admin-hint">{t('admin.bonos_comprados.f.max_iniciales', { max: bono.creditos_iniciales })}</small>
            </div>
            <div className="modal-campo">
              <label>{t('admin.bonos_comprados.f.fecha_caducidad')}</label>
              <input
                type="date"
                value={form.fecha_caducidad}
                onChange={(e) => setForm((f) => ({ ...f, fecha_caducidad: e.target.value }))}
              />
            </div>
            <div className="modal-campo full">
              <label>{t('admin.bonos_comprados.col.estado')}</label>
              <select value={form.estado} onChange={(e) => setForm((f) => ({ ...f, estado: e.target.value }))}>
                <option value="activo">{t('admin.bonos_comprados.estados.activo')}</option>
                <option value="caducado">{t('admin.bonos_comprados.estados.caducado')}</option>
                <option value="cancelado">{t('admin.bonos_comprados.estados.cancelado')}</option>
              </select>
            </div>
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}

          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
